import React from 'react';
import { observer } from '../../../shared/observer';
import { runBusy, isBusy } from '../../../shared/busy';
import { curFormat } from '../../../shared/format';
import { DatePickerDialog } from './DatePickerDialog';

// Выбор даты и периода доставки. Повторяет UX мобильного приложения
// (`client_app/src/screens/Cart/Checkout/index.tsx`):
//   - кнопка-триггер с иконкой 📅 + датой/«Выбрать дату» → модалка с
//     месячным календарём (DatePickerDialog);
//   - chip-ряд периодов из `orderDatePeriod.periods`, активный = чёрная заливка;
//   - блок «Доставка NNN ₽» под чипами, если у выбранного периода
//     `delivery_price > 0`;
//   - блокирующая модалка «нельзя доставить на эту дату» (порт
//     `client_app/.../Cart/Checkout/CantDeliverOnDate`) с кнопкой «Хорошо»,
//     которая зовёт `updateDate(can_date)`.
//
// При смене `selectedAddressDistrictId` зовёт `updateMinDateForOrder`.

function formatDateDisplay(iso) {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return m[3] + '.' + m[2] + '.' + m[1];
}

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3 9h18" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const TruckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 7h11v9H3z" stroke="currentColor" strokeWidth="1.6" />
    <path d="M14 10h4l3 3v3h-7z" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="7" cy="17.5" r="1.8" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="17" cy="17.5" r="1.8" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

class DateSlotView extends React.Component {
  state = { dialogOpen: false, busy: {}, slotsError: null };

  componentWillReceiveProps(next) {
    const districtId = next.order.selectedAddressDistrictId;
    // Сравниваем с последним «целевым» районом (в очереди или уже
    // загружаемым): если запрос слотов сейчас занят, новый выбор не
    // теряется, а дозагружается после завершения (loadSlotsForDistrict).
    const target = this._pendingDistrictId || this._lastDistrictId;
    if (districtId && districtId !== target) {
      this._pendingDistrictId = districtId;
      this.loadSlotsForDistrict();
    }
  }

  loadSlotsForDistrict = () => {
    if (!this._pendingDistrictId || isBusy(this, 'slots')) return;
    const districtId = this._pendingDistrictId;
    this._pendingDistrictId = null;
    if (districtId === this._lastDistrictId) return; // уже загружали этот район
    this._lastDistrictId = districtId;
    this.setState({ slotsError: null });
    runBusy(this, 'slots', () => this.props.order.updateMinDateForOrder(districtId, ''))
      .catch(() => this.setState({ slotsError: 'Не удалось загрузить даты доставки' }))
      .then(this.loadSlotsForDistrict);
  };

  openDialog = () => {
    if (isBusy(this, 'slots')) return;
    this.setState({ dialogOpen: true });
  };
  closeDialog = () => this.setState({ dialogOpen: false });

  notifyChanged() {
    if (this.props.onChange) this.props.onChange();
  }

  pickDate = (iso) => {
    if (isBusy(this, 'slots')) return;
    this.setState({ dialogOpen: false, slotsError: null });
    runBusy(this, 'slots', async () => {
      await this.props.order.updateDate(iso);
      this.notifyChanged();
    }).catch(() => this.setState({ slotsError: 'Не удалось выбрать дату' }))
      .then(this.loadSlotsForDistrict);
  };

  pickPeriod = (p) => {
    if (isBusy(this, 'slots')) return;
    this.props.order.setFieldsToUpdateOrder({
      time_from: p.period[0],
      time_to: p.period[1],
    });
    this.notifyChanged();
  };

  confirmCantDeliver = () => {
    const cant = this.props.order.cantDeliverOnDate || {};
    if (!cant.can_date) {
      this.props.order.resetDateErrors();
      return;
    }
    // Если и предложенную дату не удалось применить — закрываем модалку,
    // чтобы не запирать пользователя в диалоге без выхода.
    runBusy(this, 'confirm', async () => {
      await this.props.order.updateDate(cant.can_date);
      this.notifyChanged();
    }).catch(() => this.props.order.resetDateErrors());
  };

  // Страховка: если бекенд вдруг отдал ошибку без can_date, не блокируем UI.
  dismissCantDeliver = () => {
    if (isBusy(this, 'confirm')) return;
    this.props.order.resetDateErrors();
  };

  render() {
    const odp = this.props.order.orderDatePeriod || {};
    const fields = this.props.order.fieldsToUpdateOrder;
    const periods = odp.periods || [];
    const date = fields.date || odp.date || '';
    const cant = this.props.order.cantDeliverOnDate || { date: '' };
    const cantActive = !!cant.date;

    // Как в мобильном (client_app/.../Cart/Checkout/index.tsx:379) — секция
    // даты/периода появляется только после выбора адреса и только для
    // delivery_type === 'delivery'.
    if (!fields.address_id || fields.delivery_type !== 'delivery') {
      return null;
    }

    const cur = (this.props.auth && this.props.auth.companyInfo
      && this.props.auth.companyInfo.currency_hash
      && this.props.auth.companyInfo.currency_hash.symbol_html) || '';

    const activePeriod = periods.find((p) =>
      p.period[0] === fields.time_from && p.period[1] === fields.time_to
    );
    const deliveryPrice = activePeriod ? activePeriod.delivery_price : 0;

    const freeDelivery = this.props.cart.cart.free_delivery;

    const slotsLoading = isBusy(this, 'slots');

    // «Продажа не сегодня»: сервер уже убрал сегодня из дат (orderDatePeriod.no_today_sale),
    // подсказку и её текст настраивают в «Настройках заказов», как в приложении.
    const info = (this.props.auth && this.props.auth.companyInfo) || {};
    const noTodayHint = odp.no_today_sale && info.no_today_sale_checkout_hint !== false
      ? ((info.no_today_sale_checkout_hint_text || '').trim() || 'В корзине есть товары, которые нельзя доставить сегодня')
      : null;

    return (
      <section className="ng-checkout__section">
        <label>Дата и время</label>
        <button
          type="button"
          className={'ng-date-trigger' + (slotsLoading ? ' is-busy' : '')}
          disabled={slotsLoading}
          onClick={this.openDialog}
        >
          <span className="ng-date-trigger__icon"><CalendarIcon /></span>
          {date
            ? <span className="ng-date-trigger__text">{formatDateDisplay(date)}</span>
            : <span className="ng-date-trigger__placeholder">Выбрать дату</span>
          }
        </button>

        {noTodayHint && <p className="ng-no-today-hint">{noTodayHint}</p>}

        {slotsLoading && (
          <div className="ng-period-list__label">
            <span className="ng-loading-hint">Загружаем даты доставки…</span>
          </div>
        )}

        {!slotsLoading && this.state.slotsError && (
          <div className="order-message">{this.state.slotsError}</div>
        )}

        {!slotsLoading && periods.length > 0 && (
          <div>
            <div className="ng-period-list__label">Период доставки</div>
            <div className="ng-period-list">
              {periods.map((p) => {
                const active = activePeriod && activePeriod.id === p.id;
                return (
                  <button
                    type="button"
                    key={p.id}
                    className={'ng-period' + (active ? ' ng-period--active' : '')}
                    onClick={() => this.pickPeriod(p)}
                  >
                    {(p.title || '').replace(/ /g, '')}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!deliveryPrice && activePeriod && freeDelivery && freeDelivery.qualified && (
          <div className="ng-delivery-price">
            <TruckIcon />
            <span>Доставка бесплатно</span>
          </div>
        )}

        {deliveryPrice > 0 && (
          <div className="ng-delivery-price">
            <TruckIcon />
            <span>Доставка: {curFormat(deliveryPrice)} {cur}</span>
          </div>
        )}

        {this.state.dialogOpen && (
          <DatePickerDialog
            value={date}
            minDate={this.props.order.minDateForOrder || ''}
            onPick={this.pickDate}
            onClose={this.closeDialog}
          />
        )}

        {cantActive && (
          <div className="dialog">
            <div className="dialog__bg" onClick={this.dismissCantDeliver} />
            <div className="dialog__content">
              <div className="ng-cant-deliver">
                <h3 className="ng-cant-deliver__title">
                  {cant.message || ('Доставка возможна с ' + formatDateDisplay(cant.can_date))}
                </h3>
                <button
                  type="button"
                  className={'btn btn--block' + (isBusy(this, 'confirm') ? ' is-busy' : '')}
                  disabled={isBusy(this, 'confirm')}
                  onClick={this.confirmCantDeliver}
                >
                  Хорошо
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  }
}

export const DateSlot = observer(DateSlotView, ['orderStore', 'authStore', 'cartStore']);



// WEBPACK FOOTER //
// ./app/javascript/website_newgen/js/components/Cart/Checkout/DateSlot.jsx